// Everything the office sees is Europe/London wall clock, so that is the only zone
// availability is ever reasoned about in. The crew member's device zone decides nothing:
// the number they picked on the time spinner is the UK time they meant.
export const LONDON = 'Europe/London'

export type DayKey = string // 'yyyy-MM-dd'

const pad = (n: number) => `${n}`.padStart(2, '0')

/**
 * The zone maths is done with Intl rather than date-fns-tz, which cannot run in this bundle.
 *
 * metro.config.js sets `disableHierarchicalLookup`, so Metro never looks inside
 * node_modules/date-fns-tz/node_modules and resolves `date-fns` to the workspace root copy,
 * which is v2. date-fns-tz v3 expects v3's module shape, so every one of its functions throws
 * `format_1.format is not a function` the moment it runs on the device. Hermes carries Intl
 * with full timezone data, and it is what date-fns-tz consults for offsets anyway.
 */
const londonFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: LONDON,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

type LondonParts = { year: number; month: number; day: number; hour: number; minute: number; second: number }

const partsInLondon = (date: Date): LondonParts => {
  const found: { [type: string]: string } = {}

  londonFormatter.formatToParts(date).forEach(part => {
    if (part.type !== 'literal') {
      found[part.type] = part.value
    }
  })

  return {
    year: Number(found.year),
    month: Number(found.month),
    day: Number(found.day),
    // Some engines render midnight as 24 even under h23.
    hour: Number(found.hour) % 24,
    minute: Number(found.minute),
    second: Number(found.second),
  }
}

/** London's offset from UTC at a given instant, in milliseconds. +3600000 through BST. */
const londonOffset = (date: Date): number => {
  const parts = partsInLondon(date)
  const wallAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)

  // formatToParts has no milliseconds, so compare against the same whole second.
  return wallAsUtc - Math.floor(date.getTime() / 1000) * 1000
}

const toDate = (date: Date | string): Date => (typeof date === 'string' ? new Date(date) : date)

/** The London day an instant falls on. Never use toISOString() for this — it gives the UTC day. */
export const londonDayKey = (date: Date | string): DayKey => {
  const parts = partsInLondon(toDate(date))
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

/** The London wall-clock time an instant falls on, as 'HH:mm'. */
export const londonTimeOfDay = (date: Date | string): string => {
  const parts = partsInLondon(toDate(date))
  return `${pad(parts.hour)}:${pad(parts.minute)}`
}

/**
 * The instant at a London wall-clock time on `day`.
 *
 * The offset depends on the answer, so it is applied twice: the first pass uses the offset in
 * force at the same clock reading in UTC, the second corrects it. They differ only on the two
 * clock-change days, which is exactly where a single pass lands an hour out.
 */
export const londonDayAtTime = (day: DayKey, hours: number, minutes: number, seconds = 0, ms = 0): Date => {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number)
  const wall = Date.UTC(year, month - 1, date, hours, minutes, seconds, ms)
  const firstPass = new Date(wall - londonOffset(new Date(wall)))

  return new Date(wall - londonOffset(firstPass))
}

/** Midnight at the start of a London day. */
export const londonStartOfDay = (day: DayKey): Date => londonDayAtTime(day, 0, 0)

/**
 * The last instant of a London day. An all-day period has to reach 23:59:59.999 or the
 * final day is excluded everywhere it is compared with `<=`.
 */
export const londonEndOfDay = (day: DayKey): Date => londonDayAtTime(day, 23, 59, 59, 999)

/** A London instant as 'dd/MM/yyyy HH:mm', for anything a person reads. */
export const formatLondonStamp = (date: Date | string): string =>
  `${londonDayKey(date).split('-').reverse().join('/')} ${londonTimeOfDay(date)}`

/**
 * A Date whose *device-local* wall clock reads the same as the instant's London wall clock.
 *
 * Only for seeding the time spinner, which renders a Date in the device zone. Never store one:
 * on a phone that is not on London time it is a different instant from the one passed in.
 */
export const londonTimeAsLocalDate = (date: Date | string): Date => {
  const [hours = 0, minutes = 0] = londonTimeOfDay(date).split(':').map(Number)
  const seeded = new Date()
  seeded.setHours(hours, minutes, 0, 0)
  return seeded
}

/**
 * Shift a day key by whole days or months. Done in UTC milliseconds / UTC calendar parts so
 * there is no DST discontinuity to fall into — a day key has no time of day to lose.
 */
export const addDaysToDayKey = (day: DayKey, days: number): DayKey => {
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number)
  const shifted = new Date(Date.UTC(y, m - 1, d) + days * 86_400_000)
  return shifted.toISOString().slice(0, 10)
}

export const addMonthsToDayKey = (day: DayKey, months: number): DayKey => {
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1 + months, d)).toISOString().slice(0, 10)
}

/**
 * Every London day key from `from` to `to` inclusive.
 *
 * Replaces the old getDatesInRange, which mutated a Date with setDate() (local time) while
 * reading it back with toISOString() (UTC). Across the spring clock change that emitted the
 * same day twice and shifted everything after it by one.
 */
export const londonDaysInRange = (from: Date, to: Date): DayKey[] => {
  const endKey = londonDayKey(to)
  const days: DayKey[] = []
  let key = londonDayKey(from)

  // Guard against a reversed or absurd range rather than looping forever.
  while (key <= endKey && days.length < 1000) {
    days.push(key)
    key = addDaysToDayKey(key, 1)
  }

  return days
}
